import { giphyDarkGrey, giphyLightestGrey, giphyLightGrey } from '@giphy/js-brand'
import React, { SyntheticEvent, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM } from '../constants'
import { secondsToTimestamp } from '../utils/text-utils'

const Container = styled.div`
    background: #000;
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    display: flex;
    height: 36px;
    width: 100px;
`

const Input = styled.input`
    background: ${giphyDarkGrey};
    border-radius: inherit;
    color: #fff;
    flex-shrink: 0;
    font-size: 15px;
    font-weight: bold;
    height: 100%;
    line-height: 1;
    margin: 0;
    text-align: center;
    width: 75px;
`

const Buttons = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
`

const Button = styled.div`
    align-items: center;
    cursor: pointer;
    display: flex;
    font-weight: normal;
    height: 100%;
    justify-content: center;
    user-select: none;
    width: 100%;

    i {
        color: ${giphyLightGrey};
        display: block;
        font-size: 12px;
        height: 12px;
    }

    &:hover i {
        color: #fff;
    }

    &:active i {
        color: ${giphyLightestGrey};
    }
`

type Props = {
    className?: string
    value: number
    increment?: number
    min?: number
    max: number
    onUpdate: (seconds: number) => void
}

const CreationToolsTimeInput = ({ className, value, increment = 0.1, min = 0, max, onUpdate }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        convertAndSetValue(value)
    }, [inputRef, value])

    const convertAndSetValue = time => {
        if (!inputRef.current) return
        inputRef.current.value = secondsToTimestamp(time)
    }

    const onIncrease = (amount: number) => {
        onUpdate(Math.min(max, value + amount))
    }

    const onDecrease = (amount: number) => {
        onUpdate(Math.max(min, value - amount))
    }

    const onFocus = (e: SyntheticEvent<HTMLInputElement, Event>) => {
        e.currentTarget.select()
    }

    const onBlur = (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const values = (e.currentTarget.value || '').split(':')
        let seconds = 0

        if (values.length > 1) {
            const minutes = parseInt(values[0]) || 0
            seconds = minutes * 60 + (parseFloat(values[1]) || 0)
        } else {
            seconds = parseFloat(values[0]) || 0
        }
        const newTime = Math.max(min, Math.min(max, seconds))
        convertAndSetValue(newTime)
        onUpdate(newTime)
    }

    const onKeyDown = (e: SyntheticEvent<HTMLInputElement, KeyboardEvent>) => {
        switch (e.nativeEvent.key) {
            case 'Enter':
                e.currentTarget.blur()
                break
            case 'Up':
            case 'ArrowUp':
                onIncrease(increment + (e.nativeEvent.shiftKey ? 1 : 0))
                break
            case 'Down':
            case 'ArrowDown':
                onDecrease(increment + (e.nativeEvent.shiftKey ? 1 : 0))
                break
            default:
                break
        }
    }

    return (
        <Container className={className}>
            <Input
                {...{
                    ref: inputRef,
                    type: 'text',
                    placeholder: `0:00.00`,
                    onFocus,
                    onBlur,
                    onKeyDown,
                }}
            />
            <Buttons>
                <Button onClick={() => onIncrease(increment)}>
                    <i className="ss-navigateup" />
                </Button>
                <Button onClick={() => onDecrease(increment)}>
                    <i className="ss-navigatedown" />
                </Button>
            </Buttons>
        </Container>
    )
}

export default CreationToolsTimeInput
